import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatDate, formatDateRange } from "@/lib/dateUtils";
import CycleSheet from "@/components/CycleSheet";
import "./AdminHomeExact.css";
import { CuteLoader } from "@/components/CuteLoader";

const money = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function AdminHomeExact() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hcResult, setHcResult] = useState(null);
  const [hcRunning, setHcRunning] = useState(false);
  const [cycleSheetOpen, setCycleSheetOpen] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);
  const [openHold, setOpenHold] = useState(null);

  const runHealthCheck = async () => {
    setHcRunning(true);
    try {
      const { data: r } = await api.post("/admin/health-check");
      setHcResult(r);
    } catch (e) {
      setHcResult({ failed: -1, passed: 0, failures: [] });
    } finally {
      setHcRunning(false);
    }
  };

  const openCycleSheet = async () => {
    if (!cycles.length) {
      try {
        const { data: c } = await api.get("/admin/cycles");
        setCycles(c.cycles || []);
      } catch (e) { console.warn('Admin cycles failed to load:', e); }
    }
    setCycleSheetOpen(true);
  };

  const selectCycle = (id) => {
    setCycleSheetOpen(false);
    setSelectedCycleId(id);
    setLoading(true);
    loadDashboard(id);
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async (periodId) => {
    try {
      const url = periodId ? `/admin/dashboard?period_id=${periodId}` : "/admin/dashboard";
      const { data: dashboard } = await api.get(url);
      setData(dashboard);
    } catch (error) {
      console.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return <CuteLoader />;
  }

  const currentPeriod = data.current_period || {};
  const businessData = data.business_this_period || {};
  const revenuePerHour = data.revenue_per_hour || 0;
  const needsYou = data.needs_you || {};
  const comingUp = data.coming_up || [];
  const revenueTrend = data.revenue_trend || [];
  const isManager = data.is_manager;
  const viewerName = data.viewer_name || "there";
  const anomalies = data.anomalies || [];
  const companyItems = data.company_items || [];
  const sevChip = { hold: { bg: '#F3E2DA', fg: '#8A3A22', label: 'HOLD' },
                    flag: { bg: '#F7ECD6', fg: '#8A6A1E', label: 'FLAG' },
                    note: { bg: '#E7EEE4', fg: '#3E6A3E', label: 'NOTE' } };

  const now = new Date();
  const nyNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const weekday = nyNow.toLocaleDateString('en-US', { weekday: 'long' });
  const dateStr = nyNow.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  const _h = nyNow.getHours();
  const greeting = (_h >= 5 && _h < 12) ? "Good morning" : (_h >= 12 && _h < 17) ? "Good afternoon" : "Good evening";

  const daysLeft = currentPeriod.days_remaining || 0;
  const daysUntilEnd = daysLeft <= 0 ? "pay period ends today" : `${daysLeft} day${daysLeft !== 1 ? "s" : ""} until pay period ends`;
  const statusText = currentPeriod.is_finalized ? "Finalized" : daysUntilEnd;

  return (
    <div className="admin-home-exact">
      <div className="admin-header">
        <div className="admin-eyebrow">{weekday} · {dateStr}</div>
        <h1 className="admin-greeting">{greeting}, {viewerName}.</h1>
        <button className="period-pill" data-testid="admin-period-pill" onClick={openCycleSheet}
          aria-label="Change pay cycle" title="Tap to switch pay cycle"
          style={{ cursor: 'pointer', font: 'inherit' }}>
          <span className="period-dot"></span>
          {formatDateRange(currentPeriod.start_date, currentPeriod.end_date)} · {statusText}{(!currentPeriod.is_finalized && currentPeriod.check_date) ? ` · check date ${formatDate(currentPeriod.check_date, { month: 'short', day: 'numeric' })}` : ''}
          <span className="period-pill-chev" aria-hidden="true">▾</span>
        </button>
        {selectedCycleId && (
          <button data-testid="admin-back-to-current" onClick={() => selectCycle(null)}
            style={{ marginTop: 8, display: 'block', background: 'none', border: 'none', color: '#B0905E',
                     fontFamily: 'Cormorant, serif', fontSize: 14, cursor: 'pointer', padding: 0 }}>
            ← Back to current
          </button>
        )}
        {currentPeriod.is_empty && (
          <div className="period-empty-note" data-testid="admin-empty-period-note" style={{ marginTop: 8, fontSize: 13, color: '#9C6B4A', fontStyle: 'italic' }}>
            New period — numbers arrive with your first import.
          </div>
        )}
      </div>

      <CycleSheet
        open={cycleSheetOpen}
        onClose={() => setCycleSheetOpen(false)}
        cycles={cycles}
        selectedId={selectedCycleId}
        onSelect={selectCycle}
      />

      <div className="admin-sheet">
        {!isManager && (
        <div className="admin-card" data-testid="admin-business-card">
          <h3>THE BUSINESS THIS PERIOD</h3>
          <div className="business-grid">
            <div className="stat">
              <div className="stat-label">Service revenue</div>
              <div className="stat-value">${money(businessData.service_revenue)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Retail</div>
              <div className="stat-value">${money(businessData.retail_revenue)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Tips total</div>
              <div className="stat-value">${money(businessData.tips)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Labor cost</div>
              <div className="stat-value" data-testid="labor-cost-value">{businessData.labor_cost != null ? `$${money(businessData.labor_cost)}` : 'With ADP check'}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Labor % of revenue</div>
              <div className="stat-value labor-percent">{businessData.labor_percent != null ? `${businessData.labor_percent.toFixed(0)}%` : '—'}</div>
            </div>
          </div>
          <div className="freshness">
            Booker · Homebase through {businessData.last_import_date ? formatDate(businessData.last_import_date, { month: 'short', day: 'numeric' }) : '—'} · ADP through {businessData.adp_through_date ? formatDate(businessData.adp_through_date, { month: 'short', day: 'numeric' }) : '—'}
          </div>
        </div>
        )}

        {!isManager && (
        <div className="admin-card" data-testid="admin-rev-hour-card">
          <h3>REVENUE PER CLOCKED HOUR</h3>
          <div className="revenue-hour-header">
            <div className="revenue-hour-value">${revenuePerHour.toFixed(0)}</div>
            <div className="benchmark">benchmark $60</div>
          </div>
          <div className="revenue-bar">
            <div className="revenue-fill" style={{ width: `${Math.min((revenuePerHour / 60) * 100, 100)}%` }}></div>
            <div className="benchmark-tick"></div>
          </div>
          <div className="revenue-note">Team aggregate through last shift worked</div>
        </div>
        )}

        <div className="admin-card">
          <h3>NEEDS YOU</h3>
          <div className="needs-list">
            {needsYou.recap_approvals > 0 && (
              <div className="needs-row" onClick={() => navigate('/admin/approvals')}>
                <span>Recap approvals pending</span>
                <span className="needs-count">{needsYou.recap_approvals}</span>
              </div>
            )}
            {needsYou.pto_requests > 0 && (
              <div className="needs-row" onClick={() => navigate('/admin/pto')}>
                <span>PTO requests</span>
                <span className="needs-count">{needsYou.pto_requests}</span>
              </div>
            )}
            {needsYou.insurance_reviews > 0 && (
              <div className="needs-row" onClick={() => navigate('/admin/health-reimbursement')}>
                <span>Insurance reimbursement reviews</span>
                <span className="needs-count">{needsYou.insurance_reviews}</span>
              </div>
            )}
            {needsYou.adp_awaited && (
              <div className="needs-row amber">
                <span className="amber-dot"></span>
                <span>ADP register awaited</span>
                <span className="needs-count sand">—</span>
              </div>
            )}
            {needsYou.data_flags > 0 && (
              <div className="needs-row">
                <span>NEEDS-INPUT data flags</span>
                <span className="needs-count">{needsYou.data_flags}</span>
              </div>
            )}
            {needsYou.health_check && needsYou.health_check.failed > 0 && (
              <div className="needs-row amber" data-testid="needs-data-check">
                <span className="amber-dot"></span>
                <span>Data check found {needsYou.health_check.failed} issue{needsYou.health_check.failed !== 1 ? 's' : ''}</span>
                <span className="needs-count">{needsYou.health_check.failed}</span>
              </div>
            )}
          </div>
          <button
            data-testid="run-health-check-btn"
            onClick={runHealthCheck}
            disabled={hcRunning}
            style={{ marginTop: 14, width: '100%', padding: '11px 16px', borderRadius: 10, border: '1px solid #B0905E',
                     background: hcRunning ? '#efe7db' : '#231E1A', color: hcRunning ? '#8a7355' : '#F3EEE7',
                     fontFamily: 'Cormorant, serif', fontSize: 16, letterSpacing: 0.4, cursor: hcRunning ? 'default' : 'pointer' }}>
            {hcRunning ? 'Running data health check…' : 'Run health check'}
          </button>
          {hcResult && (
            <div data-testid="health-check-result" style={{ marginTop: 10, fontSize: 13, color: hcResult.failed === 0 ? '#4a7a4a' : '#9C6B4A' }}>
              {hcResult.failed === 0
                ? `All ${hcResult.passed} invariants hold — clean.`
                : hcResult.failed < 0
                  ? 'Health check could not complete.'
                  : `${hcResult.passed} passed · ${hcResult.failed} issue${hcResult.failed !== 1 ? 's' : ''} found`}
              {hcResult.failed > 0 && (hcResult.failures || []).slice(0, 5).map((f, i) => (
                <div key={i} style={{ marginTop: 4, color: '#7a5a44' }}>· [{f.employee} / {f.screen}] {f.rule}: {f.detail}</div>
              ))}
            </div>
          )}
        </div>

        {!isManager && (anomalies.length > 0 || companyItems.length > 0 || data.is_partial) && (
        <div className="admin-card" data-testid="anomaly-panel">
          <h3>OWNER HOLDS — NEEDS YOUR DECISION{anomalies.length > 0 ? ` (${anomalies.length})` : ''}</h3>
          {data.is_partial && data.partial_note && (
            <div data-testid="admin-partial-note" style={{ marginBottom: 12, fontSize: 13, fontStyle: 'italic', color: '#9C6B4A' }}>
              {data.partial_note}
            </div>
          )}
          <div className="needs-list">
            {anomalies.map((a) => {
              const chip = sevChip[a.severity] || sevChip.note;
              const isOpen = openHold === a.code;
              return (
                <div key={a.code} data-testid={`anomaly-hold-${a.code}`} style={{ borderBottom: '1px solid #EAE1D4' }}>
                  <button
                    data-testid={`anomaly-hold-toggle-${a.code}`}
                    onClick={() => setOpenHold(isOpen ? null : a.code)}
                    style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 0',
                             background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', minHeight: 44 }}>
                    <span style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, padding: '3px 7px',
                                   borderRadius: 6, background: chip.bg, color: chip.fg }}>{chip.label}</span>
                    <span style={{ flex: 1, color: '#231E1A', fontSize: 15 }}>{a.title}</span>
                    <span aria-hidden="true" style={{ color: '#B0905E' }}>{isOpen ? '▾' : '▸'}</span>
                  </button>
                  {isOpen && (
                    <div data-testid={`anomaly-hold-body-${a.code}`} style={{ padding: '0 0 14px 0', fontSize: 13, lineHeight: 1.55, color: '#5A4636' }}>
                      {a.body}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {companyItems.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, letterSpacing: 0.6, color: '#9C6B4A', marginBottom: 6 }}>COMPANY LEVEL — NOT ATTRIBUTED</div>
              {companyItems.map((ci, i) => (
                <div key={i} data-testid={`anomaly-company-item-${i}`} style={{ fontSize: 13, color: '#5A4636', padding: '4px 0' }}>
                  Gift certificate · ${money(ci.amount)} · {formatDate(ci.date, { month: 'short', day: 'numeric' })} — {ci.note}
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        <div className="admin-card">
          <h3>COMING UP</h3>
          <div className="coming-list">
            {comingUp.map((item, i) => (
              <div key={i} className="coming-row">
                <div className="coming-date">{formatDate(item.date, { month: 'short', day: 'numeric' }).toUpperCase()}</div>
                <div className="coming-text">{item.text}</div>
              </div>
            ))}
            {comingUp.length === 0 && (
              <div className="coming-row">
                <div className="coming-text">Nothing in the next 14 days</div>
              </div>
            )}
          </div>
        </div>

        {!isManager && (
        <div className="admin-card" data-testid="admin-trend-card">
          <h3>REVENUE BY PERIOD</h3>
          <div className="revenue-trend">
            {revenueTrend.map((period, i) => {
              const maxRevenue = Math.max(...revenueTrend.map(p => p.revenue));
              const height = (period.revenue / maxRevenue) * 74;
              const isCurrent = i === revenueTrend.length - 1;
              return (
                <div key={i} className="trend-bar-wrap">
                  <div className={`trend-bar ${isCurrent ? 'current' : ''}`} style={{ height: `${height}px` }}></div>
                  <div className="trend-label">{formatDate(period.end_date, { month: 'numeric', day: 'numeric' })}</div>
                </div>
              );
            })}
          </div>
        </div>
        )}

        <div className="admin-card">
          <div className="tools-grid">
            <button className="tool-chip" onClick={() => navigate('/admin/import/hub')}>Import Data</button>
            <button className="tool-chip" onClick={() => navigate('/admin/period/new')}>New Period</button>
            <button className="tool-chip" onClick={() => navigate('/admin/team')}>Team</button>
            <button className="tool-chip" onClick={() => navigate('/admin/service-catalog')}>Service Catalog</button>
            <button className="tool-chip" onClick={() => navigate('/admin/announcements')}>Announcements</button>
            <button className="tool-chip" onClick={() => navigate('/settings')}>Settings</button>
          </div>
        </div>
      </div>
    </div>
  );
}
